import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsageInfo } from '@/utils/usageStorage';

declare global {
  interface Window {
    SpeechRecognition: typeof SpeechRecognition;
    webkitSpeechRecognition: typeof SpeechRecognition;
  }
}

export default function Home() {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [listeningText, setListeningText] = useState('');
  const [usageInfo] = useState(getUsageInfo);
  const recognitionRef = useRef<InstanceType<typeof SpeechRecognition> | null>(null);

  const handleStartListening = useCallback(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      alert('您的浏览器不支持语音识别功能，请使用 Chrome 浏览器。');
      return;
    }
    const recognition = new SpeechRecognitionAPI();
    recognition.lang = 'zh-CN';
    recognition.interimResults = true;
    recognition.continuous = true;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      setListeningText(final + interim);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
    recognitionRef.current = recognition;
    setIsListening(true);
    setListeningText('');
  }, []);

  const handleStopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
    if (listeningText.trim()) {
      setQuestion((prev) => prev + (prev ? ' ' : '') + listeningText.trim());
      setListeningText('');
    }
  }, [listeningText]);

  const handleDivination = () => {
    const finalQuestion = question.trim() || listeningText.trim();
    if (!finalQuestion) {
      alert('请输入或说出您想问卜的问题');
      return;
    }
    navigate('/divination', { state: { question: finalQuestion } });
  };

  return (
    <div className="flex flex-col items-center min-h-screen pt-10 pb-6">
      <header className="w-full text-center mb-8 animate-fade-in">
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-primary-100 mb-4 overflow-hidden">
          <img
            src="https://storage.helloreaddy.io/project_files/74e88f2c-3689-4ad7-91de-a011c3f37216/d4edc0ce-1ccb-42fa-aa9b-90a6adbe7e2d_compressed_unnamed.webp"
            alt="太极八卦图"
            className="w-full h-full object-cover"
          />
        </div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-1">易经占卜</h1>
        <p className="text-sm text-foreground-500">心诚则灵 · 古老智慧的指引</p>

        {/* 剩余次数提示 */}
        <div className="mt-3 flex items-center justify-center gap-2">
          {usageInfo.remaining > 0 ? (
            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${usageInfo.remaining <= 3 ? 'bg-accent-50 border border-accent-200' : 'bg-background-100 border border-background-200'}`}>
              <i className="ri-coins-line text-xs text-foreground-500"></i>
              <span className={`text-xs font-medium ${usageInfo.remaining <= 3 ? 'text-accent-700' : 'text-foreground-600'}`}>
                剩余 {usageInfo.remaining} 次
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-50 border border-accent-200">
              <i className="ri-error-warning-line text-xs text-accent-600"></i>
              <span className="text-xs text-accent-700 font-medium">次数已用完</span>
            </div>
          )}
          {usageInfo.hasPaid && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200">
              <i className="ri-vip-crown-line text-xs text-primary-600"></i>
              <span className="text-xs text-primary-700 font-medium">付费用户</span>
            </div>
          )}
        </div>
      </header>

      <div className="w-full bg-background-50 rounded-xl border border-background-200 p-5 mb-6 animate-slide-up">
        <label htmlFor="question-input" className="block text-sm font-medium text-foreground-700 mb-3">
          请输入您想问卜的问题
        </label>
        <textarea
          id="question-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="例如：我最近的事业发展如何？"
          rows={4}
          maxLength={500}
          className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-foreground-950 placeholder-foreground-300 resize-none focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all duration-200 text-sm"
        />
        <p className="text-xs text-foreground-400 mt-2 text-right">{question.length}/500</p>
      </div>

      <div className="w-full mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 h-px bg-background-200"></div>
          <span className="text-xs text-foreground-400 whitespace-nowrap">或使用语音输入</span>
          <div className="flex-1 h-px bg-background-200"></div>
        </div>
        <div className="flex justify-center">
          {!isListening ? (
            <button
              onClick={handleStartListening}
              className="flex items-center gap-2 px-5 py-3 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-mic-line text-lg"></i>
              </div>
              <span className="text-sm font-medium">按住说话</span>
            </button>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-accent-100 animate-pulse-glow">
                <div className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></div>
                <span className="text-sm text-accent-700 font-medium">正在聆听...</span>
              </div>
              {listeningText && (
                <p className="text-sm text-foreground-600 text-center max-w-xs bg-background-100 rounded-lg px-4 py-2">
                  {listeningText}
                </p>
              )}
              <button
                onClick={handleStopListening}
                className="px-5 py-2 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap text-sm font-medium"
              >
                停止并确认
              </button>
            </div>
          )}
        </div>
      </div>

      <button
        onClick={handleDivination}
        disabled={!question.trim() && !listeningText.trim()}
        className="w-full py-4 rounded-xl bg-primary-500 text-background-50 font-heading text-lg font-bold hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-primary-500/20 active:scale-[0.98]"
      >
        打 卦
      </button>

      <p className="text-xs text-foreground-400 mt-4 text-center">
        诚心默念问题，然后点击打卦
      </p>
    </div>
  );
}